import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  editableTaskTypes,
  taskPriorities,
  taskPriorityLabels,
  taskTypeLabels,
  type EditableTaskType,
  type TaskPriority,
} from '@brimo/shared';
import {
  Banner,
  Button,
  Combobox,
  Dialog,
  FieldGroup,
  FilterChip,
  FormRow,
  Select,
  TextField,
  Textarea,
  type ComboboxOption,
} from '../../ds/components';
import { ApiError, api, errorMessage } from '../../lib/api';
import { queryRoots } from '../../lib/events';
import { useWorkspace } from '../../lib/session';
import { useCreateTask, useTasks, type TaskDetail } from './api';
import { isValidDayKey, isValidTime, todayKey } from './dates';
import styles from './CreateTaskDialog.module.css';

interface ContactHit {
  id: string;
  firstName: string;
  lastName: string;
  lifecycle: { label: string };
  owner: { name: string } | null;
}

function useContactSearch(query: string) {
  return useQuery({
    queryKey: [...queryRoots.contacts, 'picker', query],
    queryFn: () => api<{ items: ContactHit[] }>(`/contacts?q=${encodeURIComponent(query)}&limit=8`),
    enabled: query.trim().length >= 2,
    staleTime: 30_000,
  });
}

export interface CreateTaskDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (task: TaskDetail) => void;
}

const TITLE_REQUIRED = 'Bitte einen Titel angeben.';
const DATE_REQUIRED = 'Bitte ein Datum wählen.';
const TIME_INVALID = 'Bitte eine Uhrzeit im Format HH:MM angeben.';

/**
 * `/tasks/new` — the task editor of Kontaktakte E5 as a standalone dialog: title, type, priority, due,
 * assignee and an optional contact (search over `GET /contacts?q=`). Server field errors land on the
 * fields (`assigneeId`, `dueOn`, …), everything else in the banner.
 */
export function CreateTaskDialog({ open, onClose, onCreated }: CreateTaskDialogProps) {
  const workspace = useWorkspace();
  const tasks = useTasks(false);
  const create = useCreateTask();
  const today = todayKey();
  const [title, setTitle] = useState('');
  const [type, setType] = useState<EditableTaskType>('manual');
  const [priority, setPriority] = useState<TaskPriority>('c');
  const [dueOn, setDueOn] = useState(today);
  const [dueTime, setDueTime] = useState('');
  const [assigneeId, setAssigneeId] = useState(workspace.user.id);
  const [contactId, setContactId] = useState<string | null>(null);
  const [contactQuery, setContactQuery] = useState('');
  const [description, setDescription] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const search = useContactSearch(contactQuery);
  const [selectedContact, setSelectedContact] = useState<ComboboxOption | null>(null);
  const resetMutation = create.reset;

  useEffect(() => {
    if (!open) return;
    setTitle('');
    setType('manual');
    setPriority('c');
    setDueOn(todayKey());
    setDueTime('');
    setAssigneeId(workspace.user.id);
    setContactId(null);
    setSelectedContact(null);
    setContactQuery('');
    setDescription('');
    setErrors({});
    resetMutation();
  }, [open, workspace.user.id, resetMutation]);
  const contactOptions = useMemo<ComboboxOption[]>(() => {
    const hits = search.data?.items ?? [];
    const options: ComboboxOption[] = hits.map((hit) => ({
      id: hit.id,
      label: `${hit.firstName} ${hit.lastName}`.trim(),
      description: [hit.lifecycle.label, hit.owner?.name].filter(Boolean).join(' · '),
    }));
    if (selectedContact && !options.some((option) => option.id === selectedContact.id)) {
      options.unshift(selectedContact);
    }
    return options;
  }, [search.data, selectedContact]);

  const assignees = tasks.data?.assignees;
  const me = `${workspace.user.firstName.charAt(0)}. ${workspace.user.lastName}`.trim();
  const assigneeOptions =
    assignees && assignees.length > 1
      ? assignees.map((user) => ({ value: user.id, label: user.name }))
      : null;
  const serverFields = create.error instanceof ApiError ? create.error.fields : {};
  const fieldError = (key: string) => errors[key] ?? serverFields[key];

  function submit(event: FormEvent) {
    event.preventDefault();
    const next: Record<string, string> = {};
    if (title.trim() === '') next['title'] = TITLE_REQUIRED;
    if (!isValidDayKey(dueOn)) next['dueOn'] = DATE_REQUIRED;
    if (dueTime !== '' && !isValidTime(dueTime)) next['dueTime'] = TIME_INVALID;
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    create.mutate(
      {
        title: title.trim(),
        type,
        priority,
        dueOn,
        dueTime: dueTime === '' ? null : dueTime,
        assigneeId,
        contactId,
        description: description.trim() === '' ? null : description.trim(),
      },
      { onSuccess: onCreated },
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      width={600}
      eyebrow="Task"
      title="Aufgabe anlegen"
      subtitle="erscheint in Ihrer Task-Queue und in der Timeline"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={create.isPending}>
            Abbrechen
          </Button>
          <Button type="submit" form="create-task-form" loading={create.isPending}>
            Aufgabe anlegen
          </Button>
        </>
      }
    >
      <form id="create-task-form" className={styles.form} onSubmit={submit} noValidate>
        {create.isError && Object.keys(serverFields).length === 0 ? (
          <Banner tone="danger">{errorMessage(create.error)}</Banner>
        ) : null}
        <TextField
          label="Titel"
          required
          autoFocus
          value={title}
          error={fieldError('title')}
          onChange={(event) => setTitle(event.target.value)}
        />
        <FieldGroup legend="Art">
          <div className={styles.chips} role="radiogroup" aria-label="Art">
            {editableTaskTypes.map((value) => (
              <FilterChip
                key={value}
                role="radio"
                aria-checked={type === value}
                label={taskTypeLabels[value]}
                active={type === value}
                onClick={() => setType(value)}
              />
            ))}
          </div>
        </FieldGroup>
        <FieldGroup legend="Priorität" description="bestimmt die Reihenfolge in der Queue">
          <div className={styles.chips} role="radiogroup" aria-label="Priorität">
            {taskPriorities.map((value) => (
              <FilterChip
                key={value}
                role="radio"
                aria-checked={priority === value}
                label={taskPriorityLabels[value]}
                active={priority === value}
                onClick={() => setPriority(value)}
              />
            ))}
          </div>
        </FieldGroup>
        <FormRow columns={3}>
          <TextField
            label="Termin"
            type="date"
            required
            value={dueOn}
            error={fieldError('dueOn')}
            onChange={(event) => setDueOn(event.target.value)}
          />
          <TextField
            label="Uhrzeit"
            type="time"
            value={dueTime}
            error={fieldError('dueTime')}
            onChange={(event) => setDueTime(event.target.value)}
          />
          {assigneeOptions ? (
            <Select
              label="Verantwortlich"
              options={assigneeOptions}
              value={assigneeId}
              error={fieldError('assigneeId')}
              onChange={(event) => setAssigneeId(event.target.value)}
            />
          ) : (
            <TextField label="Verantwortlich" value={me} readOnly error={fieldError('assigneeId')} />
          )}
        </FormRow>
        <Combobox
          label="Kontakt"
          optional
          placeholder="Name, Telefon, E-Mail"
          options={contactOptions}
          value={contactId}
          filter={false}
          emptyText={contactQuery.trim().length < 2 ? 'Mindestens zwei Zeichen eingeben' : 'Keine Treffer'}
          error={fieldError('contactId')}
          onQueryChange={setContactQuery}
          onChange={(id, option) => {
            setContactId(id);
            setSelectedContact(option);
          }}
        />
        <Textarea
          label="Beschreibung"
          optional
          rows={3}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />
      </form>
    </Dialog>
  );
}
